import Phaser from 'phaser';
import {
  BULLET_BUFF_FX,
  type BulletBuffKind,
} from '../config/GameConfig';
import type Bullet from '../entities/Bullet';
import type Enemy from '../entities/Enemy';
import type Player from '../entities/Player';
import type Effects from '../ui/Effects';

export interface CombatHooks {
  /** 怪物 HP <= 0 时回调（让 MainScene 走标准击杀流程：计分 / 金币 / Combo） */
  onEnemyKilled: (enemy: Enemy) => void;
  /** 暂停时跳过 DOT 结算（火焰地圈不能在 GameOver 期间继续掉血） */
  isPaused: () => boolean;
  /** 提供 combo 伤害倍率（≥10 段时给 ×1.3） */
  comboDamageMul: () => number;
}

/**
 * CombatSystem —— 玩家弹药命中处理总入口
 *
 * 把原 MainScene 的下列逻辑封装进来：
 *   - 命中伤害计算（暴击 + Combo 大段加成 + 护盾兵打折）
 *   - 子弹生命周期（穿透 → 反弹 → 销毁）
 *   - 元素 buff 触发（fire 留地圈 / ice 减速 / chain 连锁）
 *   - 吸血 roll 和 +1❤ 飘字
 *
 * 不持有 Phaser 物理组：handleBulletHitEnemy 直接接 overlap 回调里两个对象 + 当前
 * enemyGroup 的引用（用来反弹找最近敌人 / 链锁找下一个目标）。所有视觉特效由
 * MainScene 注入的 Effects / scene 引用产出。
 */
export default class CombatSystem {
  scene: Phaser.Scene;
  player: Player;
  effects: Effects;
  enemyGroup: Phaser.Physics.Arcade.Group;
  hooks: CombatHooks;

  /**
   * 在场火焰圈记录 —— 用来做"位置去重 + 总数上限"，避免多重命中时全屏橙红遮挡视野
   * 数据：圆心 + 销毁回调（lifetime 到期 / 被挤掉时统一调）
   */
  private _activeFireZones: Array<{ x: number; y: number; cleanup: () => void }> = [];
  /** 火焰圈位置去重半径（同位置 60px 内 + 300ms 内已有 → 不再画新圈，只 tick 伤害） */
  private static FIRE_DEDUPE_R2 = 60 * 60;
  /** 屏内火焰圈总数上限（超过时挤掉最老） */
  private static FIRE_MAX = 6;

  constructor(
    scene: Phaser.Scene,
    player: Player,
    effects: Effects,
    enemyGroup: Phaser.Physics.Arcade.Group,
    hooks: CombatHooks,
  ) {
    this.scene = scene;
    this.player = player;
    this.effects = effects;
    this.enemyGroup = enemyGroup;
    this.hooks = hooks;
  }

  /**
   * 玩家子弹与敌人 overlap 的统一处理入口
   *   - 命中点扣血、出飘字（暴击金色）
   *   - 子弹按穿透 → 反弹 → 销毁的优先级处理
   *   - 命中后若有元素 buff 则触发 fire/ice/chain
   *   - 击杀触发 onEnemyKilled hook + roll 吸血
   */
  handleBulletHitEnemy(bullet: Bullet, enemy: Enemy): void {
    if (!bullet.active || !enemy.active || enemy._dead) return;
    // Arcade overlap 会在子弹仍压在同一个敌人体内时连续触发；穿透弹只应对同一敌人生效一次。
    if (bullet.hitEnemies.has(enemy)) return;
    bullet.hitEnemies.add(enemy);

    // 暴击：roll 一下，命中按 critMul 放大伤害；飘字变金色
    const isCrit = this.player.critChance > 0 && Math.random() < this.player.critChance;
    let rawDmg = bullet.damage || 1;
    if (isCrit) rawDmg = Math.round(rawDmg * this.player.critMul);
    // 速射狂热：射速 ≤ 100ms 时伤害 ×1.3（鼓励堆射速）
    if (this.player.berserk && this.player.shootingDelay <= 100) {
      rawDmg = rawDmg * 1.3;
    }
    // Combo 大段加成
    rawDmg = rawDmg * this.hooks.comboDamageMul();

    // 护盾兵：非穿透弹打折，穿透弹打满
    const dmg = enemy.isGuardian
      ? enemy.guardianAdjustDamage(rawDmg, bullet.pierce > 0)
      : rawDmg;
    this.effects.damageText(
      enemy.x, enemy.y - enemy.body.halfHeight,
      Math.round(dmg),
      isCrit,
      enemy, // mergeKey: 用敌人对象本身让同帧多发命中合并显示
    );
    const dead = enemy.takeDamage(dmg);

    // 元素弹 buff：fire 留地圈 / ice 减速 / chain 连锁
    if (bullet.effect) {
      this._applyEffect(bullet.effect, enemy, dmg);
    }

    // 命中处理顺序：先穿透，再反弹（穿透优先级高 —— 穿了就别拐弯）
    if (bullet.pierce > 0) {
      bullet.pierce -= 1;
    } else if (bullet.ricochet > 0) {
      bullet.ricochet -= 1;
      this._ricochet(bullet, enemy);
    } else {
      bullet.destroy();
    }

    if (dead) {
      this.hooks.onEnemyKilled(enemy);
      // 吸血
      if (this.player.lifestealChance > 0
          && this.player.hp < this.player.maxHp
          && Math.random() < this.player.lifestealChance) {
        this.player.hp = Math.min(this.player.maxHp, this.player.hp + 1);
        this.effects.healText(this.player.x, this.player.y);
        // 心条由调用方刷新（CombatSystem 不依赖 HUD）
      }
    }
  }

  /** 反弹弹：命中后从命中点出发，朝最近的另一只敌人飞；找不到就直接朝上飞 */
  private _ricochet(bullet: Bullet, fromEnemy: Enemy): void {
    if (!bullet.body) return;
    const speed = Math.hypot(bullet.body.velocity.x, bullet.body.velocity.y) || 900;
    bullet.x = fromEnemy.x;
    bullet.y = fromEnemy.y;
    let target: Enemy | null = null;
    let bestD2 = Infinity;
    this.enemyGroup.getChildren().forEach((e) => {
      const ent = e as Enemy;
      if (!ent.active || ent._dead || ent === fromEnemy) return;
      const dx = ent.x - bullet.x;
      const dy = ent.y - bullet.y;
      const d2 = dx * dx + dy * dy;
      if (d2 < bestD2) { bestD2 = d2; target = ent; }
    });
    if (target) {
      bullet.hitEnemies.clear();
      bullet.hitEnemies.add(fromEnemy);
      const t = target as Enemy;
      const dx = t.x - bullet.x;
      const dy = t.y - bullet.y;
      const d = Math.hypot(dx, dy) || 1;
      bullet.body.setVelocity((dx / d) * speed, (dy / d) * speed);
      bullet.setRotation(Math.atan2(dy, dx) + Math.PI / 2);
    } else {
      bullet.body.setVelocity(0, -speed);
      bullet.setRotation(0);
    }
  }

  // ─── 元素 buff 触发 ──────────────────────────────────────

  private _applyEffect(effect: BulletBuffKind, hit: Enemy, baseDmg: number): void {
    if (effect === 'fire') {
      this._spawnFireZone(hit.x, hit.y);
    } else if (effect === 'ice') {
      hit.applySlow(BULLET_BUFF_FX.ice.slowMul, BULLET_BUFF_FX.ice.slowMs);
    } else if (effect === 'chain') {
      this._chainLightning(hit, baseDmg);
    }
  }

  /**
   * 火焰地圈：tickIntervalMs 周期对圈内怪物 DOT；不走暴击
   *
   * 防遮挡：多重命中时同位置不再叠新圈（位置去重）+ 屏内总数封顶 6 个，
   * 否则 9 弹 + 穿透 + 多重 + 火焰弹会把屏幕铺满橙红圆，看不到怪。
   */
  private _spawnFireZone(x: number, y: number): void {
    const r = BULLET_BUFF_FX.fire.radius;

    // 位置去重：60px 半径内已有火圈 → 跳过（伤害已经被现有圈的 tickEv 覆盖了）
    for (const fz of this._activeFireZones) {
      const dx = fz.x - x;
      const dy = fz.y - y;
      if (dx * dx + dy * dy < CombatSystem.FIRE_DEDUPE_R2) return;
    }
    // 总数上限：超出就挤掉最老一个
    while (this._activeFireZones.length >= CombatSystem.FIRE_MAX) {
      const oldest = this._activeFireZones.shift();
      oldest?.cleanup();
    }

    // 填充透明度从 0.18 降到 0.12，描边略压暗，重叠时也不致密不可见
    const ring = this.scene.add.circle(x, y, r, 0xff6a3c, 0.12)
      .setStrokeStyle(3, 0xff8a3c, 0.7)
      .setDepth(40);
    this.scene.tweens.add({
      targets: ring,
      alpha: { from: 0.4, to: 0.15 },
      duration: 260,
      yoyo: true,
      repeat: -1,
    });
    const tickEv = this.scene.time.addEvent({
      delay: BULLET_BUFF_FX.fire.tickIntervalMs,
      loop: true,
      callback: () => {
        if (this.hooks.isPaused()) return;
        this.enemyGroup.getChildren().forEach((e) => {
          const ent = e as Enemy;
          if (!ent.active || ent._dead) return;
          const dx = ent.x - x;
          const dy = ent.y - y;
          if (dx * dx + dy * dy > r * r) return;
          const dead = ent.takeDamage(BULLET_BUFF_FX.fire.tickDamage);
          if (dead) this.hooks.onEnemyKilled(ent);
        });
      },
    });

    // 统一清理：lifetime 到期 / 被挤掉 都走这个
    let cleaned = false;
    const cleanup = () => {
      if (cleaned) return;
      cleaned = true;
      tickEv.remove();
      ring.destroy();
      const idx = this._activeFireZones.findIndex((fz) => fz.cleanup === cleanup);
      if (idx >= 0) this._activeFireZones.splice(idx, 1);
    };
    this._activeFireZones.push({ x, y, cleanup });
    this.scene.time.delayedCall(BULLET_BUFF_FX.fire.lifetimeMs, cleanup);
  }

  /** 闪电链：从 from 跳到最近 N 只怪，伤害递减 dmgFalloff，画紫色 line 视觉 */
  private _chainLightning(from: Enemy, baseDmg: number): void {
    const jumps = BULLET_BUFF_FX.chain.jumps;
    const range = BULLET_BUFF_FX.chain.range;
    const visited = new Set<Enemy>([from]);
    let prev = from;
    let dmg = baseDmg;
    for (let j = 0; j < jumps; j++) {
      dmg = dmg * BULLET_BUFF_FX.chain.dmgFalloff;
      let target: Enemy | null = null;
      let bestD2 = range * range;
      this.enemyGroup.getChildren().forEach((e) => {
        const ent = e as Enemy;
        if (!ent.active || ent._dead || visited.has(ent)) return;
        const dx = ent.x - prev.x;
        const dy = ent.y - prev.y;
        const d2 = dx * dx + dy * dy;
        if (d2 < bestD2) { bestD2 = d2; target = ent; }
      });
      if (!target) break;
      const t = target as Enemy;
      const line = this.scene.add.line(0, 0, prev.x, prev.y, t.x, t.y, 0xc0a8ff, 1)
        .setLineWidth(2.5)
        .setOrigin(0, 0)
        .setDepth(140);
      this.scene.tweens.add({
        targets: line,
        alpha: 0,
        duration: 200,
        onComplete: () => line.destroy(),
      });
      const dead = t.takeDamage(dmg);
      this.effects.damageText(t.x, t.y - t.body.halfHeight, Math.round(dmg), false, t);
      if (dead) this.hooks.onEnemyKilled(t);
      visited.add(t);
      prev = t;
    }
  }
}
